/**
 * 🎯 MISSION 33 — PHASE 6: Subscription & LLM Analytics Dashboard
 *
 * Unified admin analytics page answering 6 critical business questions:
 *  Q1: Average chat-message cost (spread & distribution)
 *  Q2: Intelligence call frequency and cost (spread & distribution)
 *  Q3: Average debrief-session cost (messages & volume)
 *  Q4: Messages from first message to first goal
 *  Q5: Goal-creation pipeline cost (goal + framework + plan + extraction)
 *  Q6: Average weekly-plan generation cost
 *
 * Tabs: Overview | Cost Distributions | Debriefs | Goal Pipeline | Journey | Tiers | High-Cost Users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { storage } from '../services/storage';
import { UnattributedBanner } from '../components/admin/UnattributedBanner';
import {
  DollarSign,
  BarChart3,
  Users,
  Target,
  Zap,
  Brain,
  Calendar,
  TrendingUp,
  AlertTriangle,
  Activity,
  MessageSquare,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// ── API ──────────────────────────────────────────────────────────────────────

import { API_BASE_URL } from '../utils/apiConfig';

async function apiFetch<T>(path: string): Promise<T> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin token not found. Please log in again.');

  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────────────

interface OverviewData {
  period_days: number;
  totals: {
    total_calls: number;
    unique_users: number;
    total_cost: number;
    avg_cost_per_call: number;
    avg_input_tokens: number;
    avg_output_tokens: number;
    avg_response_time: number;
  };
  by_function: FunctionRow[];
  by_tier: TierRow[];
  by_model: ModelRow[];
}

interface FunctionRow {
  function_id: number;
  function_name: string;
  call_count: number;
  unique_users: number;
  total_cost: number;
  avg_cost: number;
  avg_input: number;
  avg_output: number;
  avg_latency: number;
  pct_of_total: number;
}

interface TierRow {
  tier: string;
  user_count: number;
  total_calls: number;
  total_cost: number;
  avg_cost_per_user: number;
  min_cost_per_user: number;
  max_cost_per_user: number;
}

interface ModelRow {
  model: string;
  call_count: number;
  total_cost: number;
  avg_cost_per_call: number;
  avg_input: number;
  avg_output: number;
}

interface DistributionData {
  function_id: number;
  function_name: string;
  period_days: number;
  tier_filter: string | null;
  distribution: {
    call_count: number;
    unique_users: number;
    total_cost: number;
    avg_cost: number;
    median_cost: number;
    min_cost: number;
    max_cost: number;
    p10: number;
    p25: number;
    p75: number;
    p90: number;
    std_dev: number;
    avg_input_tokens: number;
    avg_output_tokens: number;
    avg_response_time: number;
    median_response_time: number;
    p90_response_time: number;
  };
  histogram: { bucket: string; count: number }[];
  daily_trend: { day: string; calls: number; cost: number; avg_cost: number }[];
  by_model: { model: string; call_count: number; avg_cost: number; total_cost: number }[];
}

interface DebriefData {
  period_days: number;
  session_analysis: {
    total_sessions: number;
    avg_calls_per_session: number;
    avg_session_cost: number;
    median_session_cost: number;
    p90_session_cost: number;
    min_session_cost: number;
    max_session_cost: number;
    total_cost: number;
    avg_input_per_session: number;
    avg_output_per_session: number;
    avg_response_time: number;
  };
  per_call: {
    total_calls: number;
    avg_call_cost: number;
    median_call_cost: number;
    p90_call_cost: number;
  };
}

interface PipelineCostData {
  period_days: number;
  pipeline_function_ids: number[];
  aggregate: { total_calls: number; total_cost: number };
  per_phase: {
    function_id: number;
    function_name: string;
    call_count: number;
    unique_users: number;
    total_cost: number;
    avg_cost: number;
    median_cost: number;
    avg_input: number;
    avg_output: number;
    avg_latency: number;
  }[];
  per_goal_pipeline: {
    total_pipelines: number;
    avg_pipeline_cost: number;
    median_pipeline_cost: number;
    p90_pipeline_cost: number;
    min_pipeline_cost: number;
    max_pipeline_cost: number;
    avg_calls_per_pipeline: number;
    total_pipeline_cost: number;
  };
}

interface JourneyData {
  period_days: number;
  messages_to_first_goal: {
    users_with_goals: number;
    users_without_goals: number;
    avg_messages: number;
    median_messages: number;
    min_messages: number;
    max_messages: number;
    avg_hours_to_goal: number;
    median_hours_to_goal: number;
    avg_cost_before_goal: number;
    total_cost_before_goals: number;
  };
  distribution: { bucket: string; user_count: number }[];
}

interface TierComparisonData {
  period_days: number;
  tiers: Record<
    string,
    {
      user_count: number;
      price: number;
      modeled_cost: number;
      actual_avg_cost: number;
      median_cost: number;
      p90_cost: number;
      min_cost: number;
      max_cost: number;
      avg_calls_per_user: number;
      total_tier_cost: number;
      margin_actual: number;
      margin_modeled: number;
      variance_from_model_pct: number;
      // Apr 16, 2026: Monthly projection fields (replaces standalone /tier-projections endpoint)
      actual_monthly_projected: number;       // Period avg cost scaled to a 30-day month
      monthly_scale_factor: number;            // 30 / period_days (e.g. 1.0 for 30d, 4.28 for 7d)
      delta_actual_vs_modeled_pct: number;    // % variance (actual_monthly - modeled) / modeled
      margin_actual_monthly_usd: number;      // price - actual_monthly_projected (dollars)
      margin_modeled_monthly_usd: number;     // price - modeled_cost (dollars)
      cache_saved?: number;                    // 2026-10-06: prompt-cache discount in the period
      function_breakdown: {
        function_id: number;
        function_name: string;
        call_count: number;
        unique_users: number;
        total_cost: number;
        avg_cost: number;
      }[];
    }
  >;
  modeled_costs_source?: string;
  audience?: { user_filter: string; paying_only: boolean };
  trial_per_trial?: { trials: number; avg_cost: number; median_cost: number; max_cost: number; modeled_cost: number };
}

interface HighCostUser {
  user_id: string;
  tier: string;
  call_count: number;
  total_cost: number;
  avg_input: number;
  avg_output: number;
  first_call: string | null;
  last_call: string | null;
  function_breakdown: {
    function_id: number;
    function_name: string;
    calls: number;
    cost: number;
  }[];
}

interface HighCostData {
  period_days: number;
  tier_filter: string | null;
  users: HighCostUser[];
}

// ── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'distributions', label: 'Cost Distributions', icon: Activity },
  { id: 'debriefs', label: 'Debriefs', icon: MessageSquare },
  { id: 'pipeline', label: 'Goal Pipeline', icon: Target },
  { id: 'journey', label: 'Journey', icon: TrendingUp },
  { id: 'tiers', label: 'Tiers', icon: Users },
  { id: 'high-cost', label: 'High-Cost Users', icon: AlertTriangle },
] as const;

type TabId = (typeof TABS)[number]['id'];

const CHART_COLORS = ['#2E5E34', '#5FA287', '#42A5A5', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

const FUNCTION_OPTIONS: { id: number; label: string; question: string }[] = [
  { id: 1, label: 'Chat Message', question: 'Q1' },
  { id: 9, label: 'Intelligence Analysis', question: 'Q2' },
  { id: 4, label: 'Weekly Plan Generation', question: 'Q6' },
  { id: 2, label: 'Post-Workout Debrief', question: 'Q3' },
  { id: 3, label: 'Goal Creation', question: 'Q5' },
  { id: 10, label: 'Premium Synthesis', question: '' },
  { id: 11, label: 'Framework Generation', question: '' },
  { id: 5, label: 'Content Extraction', question: '' },
  { id: 6, label: 'Goal Refinement', question: '' },
  { id: 8, label: 'Recalibration', question: '' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt$(v: number, digits = 4): string {
  if (v >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(digits)}`;
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

function fmtK(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

function HelpTip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block">
      <HelpCircle
        className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help ml-1"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color = 'green',
  helpText,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  color?: 'green' | 'blue' | 'amber' | 'red' | 'purple';
  helpText?: string;
}) {
  const colors = {
    green: 'text-[#1F6C4F]',
    blue: 'text-blue-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    purple: 'text-purple-700',
  };
  const bgColors = {
    green: '#E8F2ED',
    blue: '#EFF6FF',
    amber: '#FFFBEB',
    red: '#FEF2F2',
    purple: '#F5F3FF',
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 truncate flex items-center">
              {title}
              {helpText && <HelpTip text={helpText} />}
            </p>
            <p className="text-xl font-bold mt-1 truncate">{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg`} style={{ backgroundColor: bgColors[color] }}>
              <Icon className={`h-4 w-4 ${colors[color]}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Period Selector ──────────────────────────────────────────────────────────

function PeriodSelector({
  value,
  onChange,
}: {
  value: number;
  onChange: (d: number) => void;
}) {
  const options = [7, 14, 30, 60, 90];
  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
      {options.map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            value === d
              ? 'text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={value === d ? { backgroundColor: '#1F6C4F' } : {}}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Overview
// ═══════════════════════════════════════════════════════════════════════════════

function OverviewTab({ days }: { days: number }) {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<OverviewData>(`/api/admin/subscription-analytics/overview?days=${days}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  if (!data) return null;

  const t = data.totals;
  const costPerUser = t.unique_users > 0 ? t.total_cost / t.unique_users : 0;

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total LLM Cost"
          value={fmt$(t.total_cost, 2)}
          subtitle={`${fmtK(t.total_calls)} calls`}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Avg Cost / Call"
          value={fmt$(t.avg_cost_per_call)}
          subtitle={`${t.avg_input_tokens} in / ${t.avg_output_tokens} out tokens`}
          icon={Zap}
          color="blue"
        />
        <StatCard
          title="Cost / User"
          value={fmt$(costPerUser, 2)}
          subtitle={`${t.unique_users} users`}
          icon={Users}
          color="amber"
        />
        <StatCard
          title="Avg Response Time"
          value={`${t.avg_response_time.toFixed(1)}s`}
          subtitle={`${fmtK(t.total_calls)} calls`}
          icon={Activity}
          color="purple"
        />
      </div>

      {/* By Function Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost by Function</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                  <th className="pb-2 font-medium">Function</th>
                  <th className="pb-2 font-medium text-right">Calls</th>
                  <th className="pb-2 font-medium text-right">Users</th>
                  <th className="pb-2 font-medium text-right">Total Cost</th>
                  <th className="pb-2 font-medium text-right">Avg Cost</th>
                  <th className="pb-2 font-medium text-right">Avg Latency</th>
                  <th className="pb-2 font-medium text-right">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {data.by_function.map((f) => (
                  <tr key={f.function_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F3F3F3' }}>
                    <td className="py-2 font-medium">{f.function_name}</td>
                    <td className="py-2 text-right">{fmtK(f.call_count)}</td>
                    <td className="py-2 text-right">{f.unique_users}</td>
                    <td className="py-2 text-right font-medium">{fmt$(f.total_cost, 2)}</td>
                    <td className="py-2 text-right">{fmt$(f.avg_cost)}</td>
                    <td className="py-2 text-right">{f.avg_latency.toFixed(1)}s</td>
                    <td className="py-2 text-right">
                      <span className="inline-block text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#E8F2ED', color: '#1F6C4F' }}>
                        {fmtPct(f.pct_of_total)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* By Tier + By Model side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tier Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cost by Subscription Tier</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.by_tier.map((t) => (
                <div key={t.tier} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium capitalize">{t.tier}</span>
                    <span className="text-xs text-gray-500 ml-2">{t.user_count} users</span>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{fmt$(t.total_cost, 2)}</div>
                    <div className="text-xs text-gray-500">
                      {fmt$(t.avg_cost_per_user, 2)}/user avg
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Model Pie */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cost by Model</CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_model.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={data.by_model.map((m, i) => ({
                      name: m.model,
                      value: m.total_cost,
                      fill: CHART_COLORS[i % CHART_COLORS.length],
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {data.by_model.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => fmt$(v, 4)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No model data yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Cost Distributions  (Q1, Q2, Q6)
// ═══════════════════════════════════════════════════════════════════════════════

function DistributionsTab({ days }: { days: number }) {
  const [selectedFn, setSelectedFn] = useState(1);
  const [tierFilter, setTierFilter] = useState<string | null>(null);
  const [data, setData] = useState<DistributionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    const tierParam = tierFilter ? `&tier=${tierFilter}` : '';
    apiFetch<DistributionData>(
      `/api/admin/subscription-analytics/function/${selectedFn}/distribution?days=${days}${tierParam}`,
    )
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedFn, days, tierFilter]);

  useEffect(loadData, [loadData]);

  return (
    <div className="space-y-6">
      {/* Selectors */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-600">Function:</label>
        <select
          value={selectedFn}
          onChange={(e) => setSelectedFn(Number(e.target.value))}
          className="border rounded-md px-3 py-1.5 text-sm bg-white"
        >
          {FUNCTION_OPTIONS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label} {f.question && `(${f.question})`}
            </option>
          ))}
        </select>

        <label className="text-sm font-medium text-gray-600 ml-4">Tier:</label>
        <select
          value={tierFilter ?? ''}
          onChange={(e) => setTierFilter(e.target.value || null)}
          className="border rounded-md px-3 py-1.5 text-sm bg-white"
        >
          <option value="">All Tiers</option>
          <option value="trial">Trial</option>
          <option value="full">Full</option>
          <option value="extended">Extended</option>
        </select>
      </div>

      {loading && <Loading />}
      {error && <ErrorMsg msg={error} />}
      {data && !loading && (
        <>
          {/* Percentile Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard title="Avg Cost" value={fmt$(data.distribution.avg_cost)} icon={DollarSign} color="green" />
            <StatCard title="Median" value={fmt$(data.distribution.median_cost)} icon={DollarSign} color="blue" />
            <StatCard title="P10" value={fmt$(data.distribution.p10)} subtitle="Low end" color="green" />
            <StatCard title="P90" value={fmt$(data.distribution.p90)} subtitle="High end" color="amber" />
            <StatCard title="Std Dev" value={fmt$(data.distribution.std_dev)} subtitle={`${data.distribution.call_count} calls`} color="purple" />
          </div>

          {/* Response time cards */}
          <div className="grid grid-cols-3 gap-3">
            <StatCard
              title="Avg Response"
              value={`${data.distribution.avg_response_time.toFixed(1)}s`}
              icon={Activity}
              color="blue"
            />
            <StatCard
              title="Median Response"
              value={`${data.distribution.median_response_time.toFixed(1)}s`}
              color="green"
            />
            <StatCard
              title="P90 Response"
              value={`${data.distribution.p90_response_time.toFixed(1)}s`}
              color="amber"
            />
          </div>

          {/* Histogram */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cost Distribution — {data.function_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.histogram}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `${v} calls`} />
                  <Bar dataKey="count" fill="#2E5E34" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Daily Trend */}
          {data.daily_trend.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Cost Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={[...data.daily_trend].reverse()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => fmt$(v, 2)} />
                    <Tooltip formatter={(v: number) => fmt$(v as number, 4)} />
                    <Area type="monotone" dataKey="cost" stroke="#2E5E34" fill="#2E5E3420" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Model breakdown for this function */}
          {data.by_model.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Models Used — {data.function_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                        <th className="pb-2 font-medium">Model</th>
                        <th className="pb-2 font-medium text-right">Calls</th>
                        <th className="pb-2 font-medium text-right">Avg Cost</th>
                        <th className="pb-2 font-medium text-right">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_model.map((m) => (
                        <tr key={m.model} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F3F3F3' }}>
                          <td className="py-2 font-medium">{m.model}</td>
                          <td className="py-2 text-right">{fmtK(m.call_count)}</td>
                          <td className="py-2 text-right">{fmt$(m.avg_cost)}</td>
                          <td className="py-2 text-right font-medium">{fmt$(m.total_cost, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Debriefs  (Q3)
// ═══════════════════════════════════════════════════════════════════════════════

function DebriefsTab({ days }: { days: number }) {
  const [data, setData] = useState<DebriefData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<DebriefData>(`/api/admin/subscription-analytics/debrief-sessions?days=${days}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  if (!data) return null;

  const s = data.session_analysis;
  const c = data.per_call;

  return (
    <div className="space-y-6">
      <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#E8F2ED', color: '#113C2B' }}>
        <strong>Q3:</strong> How much is the average debrief session costing? A debrief session groups all LLM calls
        for one post-workout analysis (o3-mini analysis + mini synthesis).
      </div>

      {/* Session-level KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Sessions" value={s.total_sessions} icon={MessageSquare} color="green" />
        <StatCard
          title="Avg Session Cost"
          value={fmt$(s.avg_session_cost)}
          subtitle={`Median: ${fmt$(s.median_session_cost)}`}
          icon={DollarSign}
          color="blue"
          helpText="Average cost of all LLM calls grouped by a single debrief conversation."
        />
        <StatCard
          title="P90 Session Cost"
          value={fmt$(s.p90_session_cost)}
          subtitle={`Max: ${fmt$(s.max_session_cost)}`}
          icon={AlertTriangle}
          color="amber"
        />
        <StatCard
          title="Avg Calls / Session"
          value={s.avg_calls_per_session.toFixed(1)}
          subtitle={`${s.avg_input_per_session} in / ${s.avg_output_per_session} out tokens`}
          icon={Activity}
          color="purple"
        />
      </div>

      {/* Per-call stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-Call Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Total Calls</p>
              <p className="text-lg font-bold">{fmtK(c.total_calls)}</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Avg Call Cost</p>
              <p className="text-lg font-bold">{fmt$(c.avg_call_cost)}</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Median Call Cost</p>
              <p className="text-lg font-bold">{fmt$(c.median_call_cost)}</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">P90 Call Cost</p>
              <p className="text-lg font-bold">{fmt$(c.p90_call_cost)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total cost summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Debrief Cost ({days} days)</p>
              <p className="text-2xl font-bold text-[#1F6C4F]">{fmt$(s.total_cost, 2)}</p>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p>Avg response: {s.avg_response_time.toFixed(1)}s</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Goal Pipeline  (Q5)
// ═══════════════════════════════════════════════════════════════════════════════

function PipelineTab({ days }: { days: number }) {
  const [data, setData] = useState<PipelineCostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<PipelineCostData>(`/api/admin/subscription-analytics/pipeline-cost?days=${days}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  if (!data) return null;

  const g = data.per_goal_pipeline;

  return (
    <div className="space-y-6">
      <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#E8F2ED', color: '#113C2B' }}>
        <strong>Q5:</strong> How expensive are goals, frameworks, and the first set of workouts from the immediate
        plan? This combines Goal Creation + Premium Synthesis + Framework Generation + Weekly Plan + Content Extraction.
      </div>

      {/* Per-goal KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Pipelines" value={g.total_pipelines} icon={Target} color="green" />
        <StatCard
          title="Avg Pipeline Cost"
          value={fmt$(g.avg_pipeline_cost)}
          subtitle={`Median: ${fmt$(g.median_pipeline_cost)}`}
          icon={DollarSign}
          color="blue"
          helpText="Average cost of ALL LLM calls from goal creation through workout card extraction."
        />
        <StatCard
          title="P90 Pipeline Cost"
          value={fmt$(g.p90_pipeline_cost)}
          subtitle={`Max: ${fmt$(g.max_pipeline_cost)}`}
          icon={AlertTriangle}
          color="amber"
        />
        <StatCard
          title="Total Pipeline Cost"
          value={fmt$(g.total_pipeline_cost, 2)}
          subtitle={`${g.avg_calls_per_pipeline.toFixed(1)} calls avg`}
          icon={DollarSign}
          color="purple"
        />
      </div>

      {/* Per-phase breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-Phase Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                  <th className="pb-2 font-medium">Phase</th>
                  <th className="pb-2 font-medium text-right">Calls</th>
                  <th className="pb-2 font-medium text-right">Users</th>
                  <th className="pb-2 font-medium text-right">Total Cost</th>
                  <th className="pb-2 font-medium text-right">Avg Cost</th>
                  <th className="pb-2 font-medium text-right">Median</th>
                  <th className="pb-2 font-medium text-right">Avg Latency</th>
                </tr>
              </thead>
              <tbody>
                {data.per_phase.map((p) => (
                  <tr key={p.function_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F3F3F3' }}>
                    <td className="py-2 font-medium">{p.function_name}</td>
                    <td className="py-2 text-right">{fmtK(p.call_count)}</td>
                    <td className="py-2 text-right">{p.unique_users}</td>
                    <td className="py-2 text-right font-medium">{fmt$(p.total_cost, 2)}</td>
                    <td className="py-2 text-right">{fmt$(p.avg_cost)}</td>
                    <td className="py-2 text-right">{fmt$(p.median_cost)}</td>
                    <td className="py-2 text-right">{p.avg_latency.toFixed(1)}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Phase bar chart */}
      {data.per_phase.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Phase Cost Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.per_phase}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="function_name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => fmt$(v, 2)} />
                <Tooltip formatter={(v: number) => fmt$(v as number, 4)} />
                <Bar dataKey="avg_cost" name="Avg Cost" fill="#2E5E34" radius={[4, 4, 0, 0]} />
                <Bar dataKey="median_cost" name="Median" fill="#5FA287" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Journey Milestones  (Q4)
// ═══════════════════════════════════════════════════════════════════════════════

function JourneyTab({ days }: { days: number }) {
  const [data, setData] = useState<JourneyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<JourneyData>(`/api/admin/subscription-analytics/journey-milestones?days=${days}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  if (!data) return null;

  const m = data.messages_to_first_goal;

  return (
    <div className="space-y-6">
      <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#E8F2ED', color: '#113C2B' }}>
        <strong>Q4:</strong> How many average messages from the first message to the first goal? Tracks the
        onboarding-to-goal-creation journey.
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Avg Messages to Goal"
          value={m.avg_messages.toFixed(1)}
          subtitle={`Median: ${m.median_messages.toFixed(1)}`}
          icon={MessageSquare}
          color="green"
          helpText="Chat messages between a user's very first message and their first Goal Creation call."
        />
        <StatCard
          title="Avg Time to Goal"
          value={`${m.avg_hours_to_goal.toFixed(1)}h`}
          subtitle={`Median: ${m.median_hours_to_goal.toFixed(1)}h`}
          icon={Calendar}
          color="blue"
        />
        <StatCard
          title="Avg Cost Before Goal"
          value={fmt$(m.avg_cost_before_goal)}
          subtitle={`Total: ${fmt$(m.total_cost_before_goals, 2)}`}
          icon={DollarSign}
          color="amber"
        />
        <StatCard
          title="Users With / Without Goals"
          value={`${m.users_with_goals} / ${m.users_without_goals}`}
          subtitle={`${m.users_with_goals + m.users_without_goals} total tracked`}
          icon={Users}
          color="purple"
        />
      </div>

      {/* Distribution histogram */}
      {data.distribution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Messages Before First Goal — Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => `${v} users`} />
                <Bar dataKey="user_count" fill="#2E5E34" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Range detail */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="text-gray-500">Range: </span>
              <span className="font-medium">{m.min_messages} — {m.max_messages} messages</span>
            </div>
            <div>
              <span className="text-gray-500">Conversion: </span>
              <span className="font-medium">
                {m.users_with_goals + m.users_without_goals > 0
                  ? fmtPct((m.users_with_goals / (m.users_with_goals + m.users_without_goals)) * 100)
                  : '—'}
              </span>
              <span className="text-xs text-gray-400 ml-1">created a goal</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Tier Comparison
// ═══════════════════════════════════════════════════════════════════════════════

function TiersTab({ days }: { days: number }) {
  const [data, setData] = useState<TierComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTier, setExpandedTier] = useState<string | null>(null);
  // 2026-10-06: real store subscribers by default — admin grants, testers and
  // pre-trial sign-ups otherwise count as "customers" of a tier.
  const [payingOnly, setPayingOnly] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<TierComparisonData>(
      `/api/admin/subscription-analytics/tier-comparison?days=${days}&user_filter=external&paying_only=${payingOnly}`,
    )
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days, payingOnly]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  if (!data) return null;

  const tierEntries = Object.entries(data.tiers);
  const trial = data.trial_per_trial;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6 text-sm text-gray-700 space-y-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="font-semibold text-gray-900">Actual AI cost per subscriber vs what we projected</p>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={payingOnly} onChange={(e) => setPayingOnly(e.target.checked)} />
              Paying subscribers only (bought through the App Store / Play)
            </label>
          </div>
          <p>
            <span className="font-medium">Actual cost (mo)</span> is what each subscriber's AI calls cost in the period,
            scaled to 30 days. <span className="font-medium">Modeled cost</span> is the projection from the tiers doc
            ({data.modeled_costs_source ?? 'static benchmark'}). Below the modeled number means we spend less per
            subscriber than projected. Margins here are price minus AI cost only — app-store fees and servers are not included.
          </p>
          {trial && trial.trials > 0 && (
            <p>
              <span className="font-medium">Trials:</span> {trial.trials} completed 7-day trials in the period cost
              {' '}{fmt$(trial.avg_cost, 2)} each on average (median {fmt$(trial.median_cost, 2)}, most {fmt$(trial.max_cost, 2)}),
              {' '}against {fmt$(trial.modeled_cost, 2)} projected per trial.
            </p>
          )}
        </CardContent>
      </Card>

      {tierEntries.map(([tierName, t]) => (
        <Card key={tierName}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base capitalize">{tierName} Tier</CardTitle>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-500">{t.user_count} users</span>
                {t.price > 0 && (
                  <span
                    className={`font-medium ${t.margin_actual >= 0 ? 'text-[#1F6C4F]' : 'text-red-600'}`}
                  >
                    Margin: {fmtPct(t.margin_actual)}
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Avg Cost / User</p>
                <p className="text-lg font-bold">{fmt$(t.actual_avg_cost)}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Median</p>
                <p className="text-lg font-bold">{fmt$(t.median_cost)}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">P90</p>
                <p className="text-lg font-bold">{fmt$(t.p90_cost)}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Total Tier Cost</p>
                <p className="text-lg font-bold">{fmt$(t.total_tier_cost, 2)}</p>
                {(t.cache_saved ?? 0) > 0 && (
                  <p className="text-xs text-green-700 mt-1">cache saved {fmt$(t.cache_saved ?? 0, 2)}</p>
                )}
              </div>
            </div>

            {/* Monthly Projection vs Pricing */}
            {t.price > 0 && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: '#E8F2ED' }}>
                <div className="flex justify-between items-baseline mb-2">
                  <span className="font-semibold text-gray-700">Monthly Margin Projection</span>
                  <span className="text-xs text-gray-500">
                    Period {data.period_days}d × {t.monthly_scale_factor.toFixed(2)} → 30d
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Subscription Price (mo)</span>
                  <span className="font-medium">{fmt$(t.price, 2)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Modeled Cost (mo)</span>
                  <span className="font-medium">{fmt$(t.modeled_cost, 2)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Actual Cost (mo, projected)</span>
                  <span className="font-medium">{fmt$(t.actual_monthly_projected, 2)}</span>
                </div>
                <div className="flex justify-between pt-1 mt-1" style={{ borderTop: '1px solid #C6DDD2' }}>
                  <span className="text-gray-600">Margin (modeled)</span>
                  <span className={`font-medium ${t.margin_modeled_monthly_usd >= 0 ? 'text-[#1F6C4F]' : 'text-red-600'}`}>
                    {fmt$(t.margin_modeled_monthly_usd, 2)} ({fmtPct(t.margin_modeled)})
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Margin (actual projected)</span>
                  <span className={`font-medium ${t.margin_actual_monthly_usd >= 0 ? 'text-[#1F6C4F]' : 'text-red-600'}`}>
                    {fmt$(t.margin_actual_monthly_usd, 2)} ({fmtPct(t.margin_actual)})
                  </span>
                </div>
                <div className="flex justify-between pt-1 mt-1" style={{ borderTop: '1px solid #C6DDD2' }}>
                  <span className="text-gray-600">Actual vs Modeled Delta</span>
                  <span
                    className={`font-medium ${t.delta_actual_vs_modeled_pct <= 0 ? 'text-[#1F6C4F]' : 'text-red-600'}`}
                  >
                    {t.delta_actual_vs_modeled_pct > 0 ? '+' : ''}
                    {fmtPct(t.delta_actual_vs_modeled_pct)}
                  </span>
                </div>
              </div>
            )}

            {/* Function breakdown toggle */}
            {t.function_breakdown.length > 0 && (
              <>
                <button
                  className="text-sm flex items-center gap-1" style={{ color: '#1F6C4F' }}
                  onClick={() => setExpandedTier(expandedTier === tierName ? null : tierName)}
                >
                  {expandedTier === tierName ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {expandedTier === tierName ? 'Hide' : 'Show'} function breakdown
                </button>
                {expandedTier === tierName && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                          <th className="pb-2 font-medium">Function</th>
                          <th className="pb-2 font-medium text-right">Calls</th>
                          <th className="pb-2 font-medium text-right">Users</th>
                          <th className="pb-2 font-medium text-right">Total</th>
                          <th className="pb-2 font-medium text-right">Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.function_breakdown.map((f) => (
                          <tr key={f.function_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F3F3F3' }}>
                            <td className="py-1.5">{f.function_name}</td>
                            <td className="py-1.5 text-right">{fmtK(f.call_count)}</td>
                            <td className="py-1.5 text-right">{f.unique_users}</td>
                            <td className="py-1.5 text-right font-medium">{fmt$(f.total_cost, 3)}</td>
                            <td className="py-1.5 text-right">{fmt$(f.avg_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ))}

      {tierEntries.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-12">No tier data available for this period.</p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: High-Cost Users
// ═══════════════════════════════════════════════════════════════════════════════

function HighCostTab({ days }: { days: number }) {
  const [data, setData] = useState<HighCostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<string | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    const tierParam = tierFilter ? `&tier=${tierFilter}` : '';
    apiFetch<HighCostData>(`/api/admin/subscription-analytics/high-cost-users?days=${days}${tierParam}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days, tierFilter]);

  useEffect(loadData, [loadData]);

  return (
    <div className="space-y-6">
      {/* Tier filter */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-600">Filter by tier:</label>
        <select
          value={tierFilter ?? ''}
          onChange={(e) => setTierFilter(e.target.value || null)}
          className="border rounded-md px-3 py-1.5 text-sm bg-white"
        >
          <option value="">All Tiers</option>
          <option value="trial">Trial</option>
          <option value="full">Full</option>
          <option value="extended">Extended</option>
        </select>
      </div>

      {loading && <Loading />}
      {error && <ErrorMsg msg={error} />}
      {data && !loading && (
        <div className="space-y-3">
          {data.users.map((u, idx) => (
            <Card key={u.user_id}>
              <CardContent className="p-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400 w-6">#{idx + 1}</span>
                    <div>
                      <p className="font-medium text-sm font-mono">{u.user_id.slice(0, 8)}...</p>
                      <p className="text-xs text-gray-500 capitalize">{u.tier} tier</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="font-bold text-[#1F6C4F]">{fmt$(u.total_cost, 2)}</p>
                      <p className="text-xs text-gray-500">{fmtK(u.call_count)} calls</p>
                    </div>
                    {expandedUser === u.user_id ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>

                {expandedUser === u.user_id && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                      <div>
                        <span className="text-gray-500">Avg Input: </span>
                        <span className="font-medium">{fmtK(u.avg_input)} tokens</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Avg Output: </span>
                        <span className="font-medium">{fmtK(u.avg_output)} tokens</span>
                      </div>
                      <div>
                        <span className="text-gray-500">First call: </span>
                        <span className="font-medium">
                          {u.first_call ? new Date(u.first_call).toLocaleDateString() : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last call: </span>
                        <span className="font-medium">
                          {u.last_call ? new Date(u.last_call).toLocaleDateString() : '—'}
                        </span>
                      </div>
                    </div>

                    {u.function_breakdown.length > 0 && (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                            <th className="pb-1 font-medium">Function</th>
                            <th className="pb-1 font-medium text-right">Calls</th>
                            <th className="pb-1 font-medium text-right">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {u.function_breakdown.map((f) => (
                            <tr key={f.function_id} style={{ borderBottom: '1px solid #F3F3F3' }}>
                              <td className="py-1">{f.function_name}</td>
                              <td className="py-1 text-right">{f.calls}</td>
                              <td className="py-1 text-right font-medium">{fmt$(f.cost, 3)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {data.users.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-12">No user data for this period.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Shared UI ────────────────────────────────────────────────────────────────

function Loading() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="animate-spin rounded-full h-7 w-7 border-b-2" style={{ borderBottomColor: '#1F6C4F' }} />
      <span className="ml-3 text-gray-500 text-sm">Loading analytics...</span>
    </div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
      <p className="font-medium">Failed to load data</p>
      <p className="mt-1 text-red-600">{msg}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export const AdminSubscriptionAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [days, setDays] = useState(30);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: '#0B1614' }}>Subscription & LLM Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Comprehensive cost analysis answering 6 critical business questions
          </p>
        </div>
        <PeriodSelector value={days} onChange={setDays} />
      </div>

      {/* Unattributed-call gap banner (only renders when ≥1% of spend lacks user_id) */}
      <UnattributedBanner days={days} />

      {/* Question Quick-Reference */}
      <div className="rounded-lg p-4 text-sm" style={{ backgroundColor: '#E8F2ED', border: 'none' }}>
        <p className="font-medium mb-2" style={{ color: '#113C2B' }}>Quick Reference — Business Questions</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs" style={{ color: '#1F6C4F' }}>
          <div><strong>Q1</strong> Chat message cost distribution → <em>Distributions</em> tab (function 1)</div>
          <div><strong>Q2</strong> Intelligence call cost distribution → <em>Distributions</em> tab (function 9)</div>
          <div><strong>Q3</strong> Average debrief session cost → <em>Debriefs</em> tab</div>
          <div><strong>Q4</strong> Messages to first goal → <em>Journey</em> tab</div>
          <div><strong>Q5</strong> Goal pipeline total cost → <em>Goal Pipeline</em> tab</div>
          <div><strong>Q6</strong> Weekly plan generation cost → <em>Distributions</em> tab (function 4)</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 pb-0.5">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                isActive
                  ? 'text-white -mb-px'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              style={isActive ? { backgroundColor: '#1F6C4F', color: '#FFF' } : {}}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && <OverviewTab days={days} />}
        {activeTab === 'distributions' && <DistributionsTab days={days} />}
        {activeTab === 'debriefs' && <DebriefsTab days={days} />}
        {activeTab === 'pipeline' && <PipelineTab days={days} />}
        {activeTab === 'journey' && <JourneyTab days={days} />}
        {activeTab === 'tiers' && <TiersTab days={days} />}
        {activeTab === 'high-cost' && <HighCostTab days={days} />}
      </div>
    </div>
  );
};
